import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export default function BundlePickerModal({
  databases,
  getDbId,
  getDbLabel,
  onCancel,
  onConfirm,
  busy,
  error,
}) {
  const { t } = useTranslation();

  const allIds = useMemo(
    () => (databases || []).map((d) => String(getDbId(d))),
    [databases, getDbId]
  );
  const [selected, setSelected] = useState(() => new Set(allIds));
  const [filter, setFilter] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel, busy]);

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const filtered = useMemo(() => {
    if (!filter.trim()) return databases || [];
    const f = filter.trim().toLowerCase();
    return (databases || []).filter((d) =>
      String(getDbLabel(d) ?? "").toLowerCase().includes(f) ||
      String(getDbId(d) ?? "").toLowerCase().includes(f)
    );
  }, [databases, filter, getDbId, getDbLabel]);

  function selectAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      filtered.forEach((d) => next.add(String(getDbId(d))));
      return next;
    });
  }

  function clearAll() {
    setSelected(new Set());
  }

  function handleConfirm() {
    onConfirm(Array.from(selected));
  }

  return (
    <div className="dbModalOverlay" onClick={busy ? undefined : onCancel}>
      <div className="dbModalCard" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <button
          className="drawer-close-btn drawer-close-btn-abs"
          onClick={busy ? undefined : onCancel}
          aria-label={t("common.close", { defaultValue: "Close" })}
          title={t("common.close", { defaultValue: "Close" })}
        >
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
            <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="dbModalTitle">
          {t("admin.bundlePickerTitle", { defaultValue: "Export Database Bundle" })}
        </div>
        <div className="dbModalDesc" style={{ marginBottom: 14 }}>
          {t("admin.bundlePickerDesc", {
            defaultValue: "Select the databases to include in the bundle. Indexes, properties and drop folder settings will be exported for each one.",
          })}
        </div>

        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t("admin.bundlePickerSearch", { defaultValue: "Filter databases…" })}
          style={{
            width: "100%",
            fontSize: 13,
            padding: "6px 10px",
            border: "1px solid #d1d5db",
            borderRadius: 6,
            marginBottom: 8,
            boxSizing: "border-box",
          }}
        />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>
            {t("admin.bundlePickerCount", {
              selected: selected.size,
              total: (databases || []).length,
              defaultValue: `${selected.size} of ${(databases || []).length} selected`,
            })}
          </span>
          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" className="link-btn" style={{ fontSize: 11 }} onClick={selectAllVisible}>
              {t("admin.bundlePickerSelectAll", { defaultValue: "Select all" })}
            </button>
            <button type="button" className="link-btn" style={{ fontSize: 11 }} onClick={clearAll}>
              {t("admin.bundlePickerSelectNone", { defaultValue: "Clear" })}
            </button>
          </div>
        </div>

        <div
          className="createDbFieldList"
          style={{ maxHeight: 280, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 6, padding: 8 }}
        >
          {filtered.length === 0 && (
            <div style={{ color: "#9ca3af", fontSize: 12, padding: "8px 4px" }}>
              {t("admin.bundlePickerEmpty", { defaultValue: "No databases match the filter." })}
            </div>
          )}
          {filtered.map((d) => {
            const id = String(getDbId(d));
            const label = getDbLabel(d);
            return (
              <label
                key={id}
                className="createDbFieldRow"
                style={{ alignItems: "center", cursor: "pointer", userSelect: "none" }}
              >
                <input
                  type="checkbox"
                  checked={selected.has(id)}
                  onChange={() => toggle(id)}
                  style={{ marginTop: 0, flexShrink: 0 }}
                />
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, marginLeft: 8, color: "#111" }}>
                  {label}
                </span>
              </label>
            );
          })}
        </div>

        {error && <div className="createDbFieldErr" style={{ marginTop: 10 }}>{error}</div>}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
          <button type="button" className="dbModalCancel" onClick={onCancel} disabled={busy}>
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={handleConfirm}
            disabled={busy || selected.size === 0}
          >
            {busy
              ? t("admin.bundleExporting", { defaultValue: "Exporting…" })
              : t("admin.bundleExportButton", {
                  count: selected.size,
                  defaultValue: `Export ${selected.size} database(s)`,
                })}
          </button>
        </div>
      </div>
    </div>
  );
}
